// Features
// - Suggestions follow caret (cursor)
// - Typed word always added as last option
// - No overlapping popup
// - Multiline safe
// - RTL supported (Urdu / Arabic)
// - Keyboard controls (Enter, Space, Up, Down, Esc)
// - Z-index fixed
// - Smooth UX

import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";


// -------------------------------------------------------------------
// Map frontend language code to API expected code
// -------------------------------------------------------------------
const mapLanguageCode = (code) => {
  const map = {
    hi: "hin",
    te: "tel",
    en: "eng",
    ur: "urd",
  };
  return map[code] || code;
};


const TransliterateInput = ({
  value,
  onChange,
  targetLang = "hin",
  placeholder = "",
  className = "",
}) => {

  // -------------------------------------------------------------------
  // State variables
  // -------------------------------------------------------------------
  const [suggestions, setSuggestions] = useState([]);             // API + fallback list
  const [showSuggestions, setShowSuggestions] = useState(false); // Dropdown visibility
  const [activeIndex, setActiveIndex] = useState(0);             // Arrow key selection
  const [loading, setLoading] = useState(false);                 // API loader
  const [caretPos, setCaretPos] = useState({ top: 0, left: 0 }); // Cursor position

  // -------------------------------------------------------------------
  // Refs
  // -------------------------------------------------------------------
  const textareaRef = useRef(null);   // Main textarea reference
  const containerRef = useRef(null);  // Wrapper to detect outside click

  // -------------------------------------------------------------------
  // Language settings
  // -------------------------------------------------------------------
  const typingTargetLang = mapLanguageCode(targetLang);
  const sourceLang = "eng"; // English always source for transliteration
  const isRTL = ["urd", "ara", "fas"].includes(typingTargetLang);


  // ================================================================
  // Calculate caret position (for popup placement)
  // ================================================================
  const updateCaretPosition = () => {
    const el = textareaRef.current;
    if (!el) return;

    // Clone textarea style
    const div = document.createElement("div");
    const style = window.getComputedStyle(el);
    for (const prop of style) div.style[prop] = style[prop];

    // Make invisible but measurable
    div.style.position = "absolute";
    div.style.visibility = "hidden";
    div.style.whiteSpace = "pre-wrap";
    div.style.wordWrap = "break-word";
    div.style.width = el.offsetWidth + "px";

    // Fill text till cursor
    div.textContent = el.value.substring(0, el.selectionStart);

    // Fake caret
    const span = document.createElement("span");
    span.textContent = "|";
    div.appendChild(span);

    // Render measured node and read pos
    document.body.appendChild(div);
    const { offsetTop, offsetLeft } = span;
    document.body.removeChild(div);

    // Save position (padding offsets included)
    setCaretPos({
      top: offsetTop + 25,
      left: offsetLeft + 10,
    });
  };


  // ================================================================
  // ✅ Extract word at cursor
  // ================================================================
  const getWordAtCursor = () => {
    const el = textareaRef.current;
    if (!el) return "";

    const pos = el.selectionStart;

    let start = pos;
    while (start > 0 && /[a-zA-Z0-9]/.test(value[start - 1])) start--;

    let end = pos;
    while (end < value.length && /[a-zA-Z0-9]/.test(value[end])) end++;

    const word = value.substring(start, end);
    return /^[a-zA-Z0-9]+$/.test(word) ? word : "";
  };


  // ================================================================
  // ✅ Call API when word changes
  // ================================================================
  useEffect(() => {
    const word = getWordAtCursor();

    // If cursor leaves a word -> close
    if (!word || word.trim().length === 0) {
      setShowSuggestions(false);
      setSuggestions([]);
      return;
    } else if (typingTargetLang === "eng") {
      setShowSuggestions(false);  // This hides the box completely
      setSuggestions([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.post(
          "https://transliteration.ebslab.in/api/getkbesttranstext",
          {
            text: word,
            src: sourceLang,
            tgt: typingTargetLang,
            kbest: 5,
          }
        );

        // API results
        let results = res.data?.data || [];

        // Remove empty + duplicate
        let unique = [...new Set(results.filter(Boolean))];

        // Add typed word always as LAST option
        if (!unique.includes(word)) {
          unique.push(word);
        }

        // Set suggestion list
        setSuggestions(unique);
        setShowSuggestions(true);
        setActiveIndex(0);

      } catch (err) {
        toast.error("Suggestion API Error");
        setShowSuggestions(false);
      } finally {
        setLoading(false);
      }
    }, 200);

    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [value, targetLang]);


  // ================================================================
  // ✅ Insert suggestion at cursor
  // ================================================================
  const selectSuggestion = (word) => {
    const el = textareaRef.current;
    if (!el) return;

    const pos = el.selectionStart;

    let start = pos;
    while (start > 0 && /[a-zA-Z0-9]/.test(value[start - 1])) start--;

    let end = pos;
    while (end < value.length && /[a-zA-Z0-9]/.test(value[end])) end++;

    const newValue = value.slice(0, start) + word + value.slice(end);
    onChange(newValue);

    // Restore caret
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + word.length, start + word.length);
    }, 0);

    setShowSuggestions(false);
  };


  // ================================================================
  // Keyboard navigation
  // ================================================================
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      selectSuggestion(suggestions[activeIndex] || suggestions[0]);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };


  // ================================================================
  // Hide when click outside
  // ================================================================
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);


  // ================================================================
  // UI
  // ================================================================
  return (
    <div ref={containerRef} className={`relative w-full h-80 ${className}`}>

      {/* TEXTAREA */}
      <textarea
        ref={textareaRef}
        value={value}
        placeholder={placeholder}

        onChange={(e) => {
          onChange(e.target.value);
          updateCaretPosition();  // Move popup continuously
        }}

        onKeyUp={updateCaretPosition}
        onClick={updateCaretPosition}
        onKeyDown={handleKeyDown}

        className="w-full h-full p-6 resize-none outline-none bg-white
                  border border-gray-300 rounded-lg shadow-md"

        style={{
          direction: isRTL ? "rtl" : "ltr",
          textAlign: isRTL ? "right" : "left",
          fontSize: "18px",
          fontFamily: isRTL ? "Nafees,'Jameel Noori Nastaleeq'" : "inherit",
        }}
      />


      {/* SUGGESTION POPUP */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          className="absolute bg-white border border-blue-400
                    rounded-lg shadow-2xl z-[9999]
                    min-w-[150px] max-w-xs"
          style={{
            top: caretPos.top,
            left: isRTL ? caretPos.left - 80 : caretPos.left,
          }}
        >

          {loading && <div className="p-2 text-gray-500">Loading…</div>}

          {!loading &&
            suggestions.map((word, idx) => (
              <div
                key={word}
                onClick={() => selectSuggestion(word)}
                onMouseEnter={() => setActiveIndex(idx)}
                className={`px-4 py-2 cursor-pointer
                  ${idx === activeIndex
                    ? "bg-blue-600 text-white"
                    : "hover:bg-blue-100"
                  }`}
              >
                {word}
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
};

export default TransliterateInput;
